import type { AuthResponse, Event, UserProfile, PartnerRequest, EventAttendance } from '../types'; // Store current user in memory

let currentUser: UserProfile | null = null;
let authChangeListeners: Array<(user: UserProfile | null) => void> = [];

// API Base URL
const API_BASE_URL = import.meta.env.VITE_SERVER_URL || 'http://localhost:1337';

// Site password storage key
const SITE_PASSWORD_KEY = 'wemove_site_password';

/**
 * Get stored site password
 */
export function getSitePassword(): string | null {
  return localStorage.getItem(SITE_PASSWORD_KEY);
}

/**
 * Set site password
 */
export function setSitePassword(password: string): void {
  localStorage.setItem(SITE_PASSWORD_KEY, password);
}

/**
 * Clear site password
 */
export function clearSitePassword(): void {
  localStorage.removeItem(SITE_PASSWORD_KEY);
}

/**
 * Verify site password with server
 */
export async function verifySitePassword(password: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/verify-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Site-Password': password,
      },
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Get headers with site password included
 */
function getAuthHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
  const sitePassword = getSitePassword();
  return {
    ...additionalHeaders,
    ...(sitePassword ? { 'X-Site-Password': sitePassword } : {}),
  };
}

/**
 * Sign in with email and password via server
 */
export async function signIn(email: string, password: string): Promise<UserProfile> {
  const response = await fetch(`${API_BASE_URL}/auth/signin`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Sign in failed');
  }

  const data: AuthResponse = await response.json();
  currentUser = data.user;
  notifyAuthListeners(currentUser);
  return data.user;
}

/**
 * Create user account via server
 */
export async function signUp(
  email: string,
  password: string,
  displayName?: string,
  username?: string,
  wsdcId?: string,
  location?: string,
  divisions?: Array<{ division: string; role: string }>
): Promise<UserProfile> {
  const response = await fetch(`${API_BASE_URL}/auth/signup`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({
      email,
      password,
      displayName,
      username,
      wsdcId,
      location,
      divisions,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Sign up failed');
  }

  const data: AuthResponse = await response.json();
  currentUser = data.user;
  notifyAuthListeners(currentUser);
  return data.user;
}

/**
 * Sign out current user
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
    });
  } catch (error) {
    console.error('Server logout failed:', error);
  }

  currentUser = null;
  notifyAuthListeners(null);
}

/**
 * Get current user - checks server for authenticated user if not in memory
 */
export function getCurrentUser(): UserProfile | null {
  return currentUser;
}

/**
 * Initialize auth state from server (call on app startup)
 * This restores the session after page refresh by checking Firebase auth state on server
 */
export async function initializeAuth(): Promise<UserProfile | null> {
  if (currentUser) return currentUser;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      // Not authenticated or error - that's fine, just return null
      return null;
    }

    const data: AuthResponse = await response.json();
    currentUser = data.user;
    notifyAuthListeners(currentUser);
    return data.user;
  } catch (error) {
    console.error('Failed to restore auth session:', error);
    return null;
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChange(callback: (user: UserProfile | null) => void): () => void {
  authChangeListeners.push(callback);

  // Immediately call with current user
  callback(currentUser);

  return () => {
    authChangeListeners = authChangeListeners.filter((listener) => listener !== callback);
  };
}

/**
 * Notify all auth change listeners
 */
function notifyAuthListeners(user: UserProfile | null) {
  authChangeListeners.forEach((listener) => listener(user));
}

/**
 * Get user profile from server
 */
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const response = await fetch(`${API_BASE_URL}/users/${uid}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 404) return null;
    const error = await response.json();
    throw new Error(error.error || 'Failed to get user profile');
  }

  const profile = await response.json();
  return profile;
}

/**
 * Update user profile via server
 */
export async function updateUserProfile(
  uid: string,
  updates: Partial<UserProfile>
): Promise<UserProfile> {
  const response = await fetch(`${API_BASE_URL}/users/${uid}`, {
    method: 'PUT',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update user profile');
  }

  const profile = await response.json();

  // Update current user if it's the same uid
  if (currentUser && currentUser.uid === uid) {
    currentUser = { ...currentUser, ...profile };
    notifyAuthListeners(currentUser);
  }

  return profile;
}

/**
 * Check session validity by attempting to get current user profile
 */
export async function checkSession(): Promise<UserProfile | null> {
  if (!currentUser) return null;

  try {
    const profile = await getUserProfile(currentUser.uid);
    if (profile) {
      currentUser = profile;
      notifyAuthListeners(currentUser);
    }
    return profile;
  } catch (error) {
    currentUser = null;
    notifyAuthListeners(null);
    return null;
  }
}

/**
 * Get all user profiles
 */
export async function getAllUsers(): Promise<UserProfile[]> {
  const response = await fetch(`${API_BASE_URL}/users`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get users');
  }

  const users = await response.json();
  return users;
}

/**
 * Get all events
 */
export async function getAllEvents(): Promise<Event[]> {
  const response = await fetch(`${API_BASE_URL}/events`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get events');
  }

  const events = await response.json();
  return events;
}

/**
 * Get event by ID
 */
export async function getEvent(eventId: string): Promise<Event> {
  const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get event');
  }

  const event = await response.json();
  return event;
}

/**
 * Create a new event
 */
export async function createEvent(eventData: Partial<Event>): Promise<Event> {
  const response = await fetch(`${API_BASE_URL}/events`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(eventData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create event');
  }

  const event = await response.json();
  return event;
}

/**
 * Update an event
 */
export async function updateEvent(eventId: string, updates: Partial<Event>): Promise<Event> {
  const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
    method: 'PUT',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update event');
  }

  const event = await response.json();
  return event;
}

/**
 * Delete an event
 */
export async function deleteEvent(eventId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete event');
  }
}

// ==========================================
// PARTNER REQUEST FUNCTIONS
// ==========================================

export type CreatePartnerRequestResponse = {
  request: PartnerRequest;
  isMutualMatch: boolean;
};

/**
 * Create a partner request
 * Returns the request and whether it created a mutual match
 */
export async function createPartnerRequest(request: {
  requesterId: string;
  requesterName: string;
  targetId: string;
  targetName: string;
  eventId: string;
  eventName: string;
  competitionId: string;
  competitionName: string;
  division: string;
  role: 'leader' | 'follower';
  message?: string;
  expiresAt?: any;
}): Promise<CreatePartnerRequestResponse> {
  const response = await fetch(`${API_BASE_URL}/partner-requests`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create partner request');
  }

  const data = await response.json();
  // Handle both old format (just request) and new format (with isMutualMatch)
  if (data.request) {
    return data;
  }
  return { request: data, isMutualMatch: false };
}

/**
 * Get partner requests for a user
 */
export async function getPartnerRequests(
  uid: string,
  type?: 'incoming' | 'outgoing' | 'all',
  status?: 'pending' | 'accepted' | 'declined' | 'expired'
): Promise<PartnerRequest[]> {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (status) params.append('status', status);

  const response = await fetch(`${API_BASE_URL}/partner-requests/user/${uid}?${params}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get partner requests');
  }

  return response.json();
}

/**
 * Get a specific partner request
 */
export async function getPartnerRequest(requestId: string): Promise<PartnerRequest> {
  const response = await fetch(`${API_BASE_URL}/partner-requests/${requestId}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get partner request');
  }

  return response.json();
}

/**
 * Accept or decline a partner request
 */
export async function updatePartnerRequestStatus(
  requestId: string,
  status: 'accepted' | 'declined'
): Promise<PartnerRequest> {
  const response = await fetch(`${API_BASE_URL}/partner-requests/${requestId}`, {
    method: 'PUT',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update partner request');
  }

  return response.json();
}

/**
 * Cancel/delete a partner request
 */
export async function deletePartnerRequest(requestId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/partner-requests/${requestId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete partner request');
  }
}

// ==========================================
// WSDC VERIFICATION FUNCTIONS
// ==========================================

/**
 * Search WSDC database by name or ID
 */
export async function searchWsdc(query: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/wsdc/search`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to search WSDC database');
  }

  return response.json();
}

/**
 * Get WSDC member details by member number
 */
export async function getWsdcMember(memberNumber: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/wsdc/member`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ num: memberNumber }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get WSDC member details');
  }

  return response.json();
}

/**
 * Verify and save WSDC ID to user profile
 */
export async function verifyWsdcId(
  uid: string,
  wsdcId: string,
  verifiedName: string,
  pointsData?: any
): Promise<UserProfile> {
  const response = await fetch(`${API_BASE_URL}/wsdc/verify/${uid}`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ wsdcId, verifiedName, pointsData }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to verify WSDC ID');
  }

  const profile = await response.json();

  // Update current user if it's the same uid
  if (currentUser && currentUser.uid === uid) {
    currentUser = { ...currentUser, ...profile };
    notifyAuthListeners(currentUser);
  }

  return profile;
}

// ==========================================
// PARTNER DISCOVERY FUNCTIONS
// ==========================================

/**
 * Find potential partners for an event/competition
 */
export async function discoverPartners(params: {
  userId: string;
  eventId: string;
  competitionId?: string;
  division?: string;
  role?: 'leader' | 'follower';
}): Promise<UserProfile[]> {
  const searchParams = new URLSearchParams();
  searchParams.append('userId', params.userId);
  searchParams.append('eventId', params.eventId);
  if (params.competitionId) searchParams.append('competitionId', params.competitionId);
  if (params.division) searchParams.append('division', params.division);
  if (params.role) searchParams.append('role', params.role);

  const response = await fetch(`${API_BASE_URL}/discover/partners?${searchParams}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to discover partners');
  }

  return response.json();
}

// ==========================================
// EVENT ATTENDANCE FUNCTIONS
// ==========================================

/**
 * Add or update event attendance for a user
 */
export async function addEventAttendance(
  uid: string,
  eventAttendance: EventAttendance
): Promise<UserProfile> {
  const response = await fetch(`${API_BASE_URL}/users/${uid}/events`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(eventAttendance),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add event attendance');
  }

  const profile = await response.json();

  // Update current user if it's the same uid
  if (currentUser && currentUser.uid === uid) {
    currentUser = { ...currentUser, ...profile };
    notifyAuthListeners(currentUser);
  }

  return profile;
}

/**
 * Remove event attendance for a user
 */
export async function removeEventAttendance(uid: string, eventId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/users/${uid}/events/${eventId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove event attendance');
  }

  // Update current user if it's the same uid
  if (currentUser && currentUser.uid === uid) {
    currentUser = {
      ...currentUser,
      events: (currentUser.events || []).filter((e) => e.eventId !== eventId),
    };
    notifyAuthListeners(currentUser);
  }
}

// ==========================================
// PRIVACY & ACCOUNT FUNCTIONS
// ==========================================

/**
 * Block a user
 */
export async function blockUser(uid: string, blockedUid: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/users/${uid}/block/${blockedUid}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to block user');
  }

  // Update current user
  if (currentUser && currentUser.uid === uid) {
    currentUser = {
      ...currentUser,
      blockedUserIds: [...(currentUser.blockedUserIds || []), blockedUid],
    };
    notifyAuthListeners(currentUser);
  }
}

/**
 * Unblock a user
 */
export async function unblockUser(uid: string, blockedUid: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/users/${uid}/block/${blockedUid}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unblock user');
  }

  // Update current user
  if (currentUser && currentUser.uid === uid) {
    currentUser = {
      ...currentUser,
      blockedUserIds: (currentUser.blockedUserIds || []).filter((id) => id !== blockedUid),
    };
    notifyAuthListeners(currentUser);
  }
}

/**
 * Request account deletion (GDPR compliance)
 */
export async function requestAccountDeletion(uid: string, reason?: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/users/${uid}/request-deletion`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to request account deletion');
  }
}

/**
 * Export user data (GDPR compliance)
 */
export async function exportUserData(uid: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/users/${uid}/export`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to export user data');
  }

  return response.json();
}

/**
 * Trigger WSDC event scrape (Admin)
 */
export async function scrapeWsdcEvents(): Promise<{
  message: string;
  events: Array<{ name: string; date: string; location: string }>;
}> {
  const response = await fetch(`${API_BASE_URL}/admin/scrape-events`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to scrape events');
  }

  return response.json();
}
